import { TSize } from 'src/interfaces/global';
import { INGText } from 'src/styles/theme/components/NGText/NGText.type';
import { defaultTheme } from 'src/styles/theme/theme';
import { css, DefaultTheme, FlattenSimpleInterpolation } from 'styled-components';
import { THiddenRange } from '../../../types/global';
import { EItemRarity, EMediaQuery, ESize, ETextColor, EThemeColor, ETree } from './enums';

export const mq = (
	mediaQuery: EMediaQuery | string,
	children: string | FlattenSimpleInterpolation,
	minOrMax = 'max'
) => {
	return `@media only screen and (${minOrMax}-width: ${mediaQuery}) {
		${children}
	}`;
};

export const mqRange = (
	minWidth: EMediaQuery | string,
	maxWidth: EMediaQuery | string,
	children: string | FlattenSimpleInterpolation
) => {
	return `@media only screen and (min-width: ${minWidth}) and (max-width: ${maxWidth}) {
		${children}
	}`;
};

export const mqV = (
	mediaQuery: EMediaQuery | string,
	children: string | FlattenSimpleInterpolation,
	minOrMax = 'max'
) => {
	return `@media only screen and (${minOrMax}-height: ${mediaQuery}) {
		${children}
	}`;
};

/**
 * get a size value as string from a number, a string or an ESize enum
 * @param size the size to get
 * @returns if size is a number, returns a string with the size + 'px', if size is a string, returns the string, if size is an ESize enum, returns the value of the enum
 */
export const getSpacing = (size: TSize): string => {
	try {
		// check if size is part of ESize
		if (Object.values(ESize).includes(size as ESize)) {
			return defaultTheme.spacing[size as ESize];
		}

		// check if size is a number
		if (typeof size === 'number') {
			// detect if number is a fraction
			if (size > 0 && size < 1) {
				return `${Math.floor(size * 100)}%`;
			}

			return `${size}px`;
		}

		// check if size is a string
		if (typeof size === 'string') {
			return size;
		}

		return '';
	} catch (err) {
		console.error('getSpacing', err);
		return '';
	}
};

// add transition
export const addTransition = (
	target: string = 'all',
	duration: number = 0.4,
	delay: number = 0,
	timingFunction: string = 'ease'
) => {
	return `
		@media screen and (prefers-reduced-motion: no-preference) {
			transition: ${target} ${duration}s ${delay + 's'} ${timingFunction};
		}
	`;
};

const getNormalStyles = (size: ESize) => {
	switch (size) {
		case ESize.xs:
			return `
        font-size: 0.625rem;
        line-height: 1rem;
      `;
		case ESize.s:
			return `
        font-size: 0.75rem;
        line-height: 1.125rem;
      `;
		case ESize.m:
			return `
        font-size: 0.9rem;
        line-height: 1.35rem;
      `;
		case ESize.l:
			return `
        font-size: 1rem;
        line-height: 1.5rem;
      `;
		case ESize['4xl']:
			return `
        font-size: 3rem;
        line-height: 3.6rem;
      `;
		default:
			return ``;
	}
};

const getMediaStyles = (size: ESize) => {
	switch (size) {
		case ESize.xs:
			return `
        font-size: 0.625rem;
        line-height: 1rem;
      `;
		case ESize.s:
			return `
        font-size: 0.75rem;
        line-height: 1rem;
      `;
		case ESize.m:
			return `
        font-size: 0.875rem;
        line-height: 1.22rem;
      `;
		case ESize.l:
			return `
        font-size: 1rem;
        line-height: 1.35rem;
      `;
		default:
			return ``;
	}
};

// set font size for normal text
export const setFontSize = (p: DefaultTheme & INGText) => {
	return `
	${p.size ? `${getNormalStyles(p.size)} ${mq(EMediaQuery.md, getMediaStyles(p.size))}` : ''}

	${p.xlSize ? mq(EMediaQuery.xl, getNormalStyles(p.xlSize)) : ''}
	${p.sxlSize ? mq(EMediaQuery.sxl, getNormalStyles(p.sxlSize)) : ''}
	${p.lgSize ? mq(EMediaQuery.lg, getNormalStyles(p.lgSize)) : ''}
	${p.mdSize ? mq(EMediaQuery.md, getMediaStyles(p.mdSize)) : ''}
	${p.smSize ? mq(EMediaQuery.sm, getMediaStyles(p.smSize)) : ''}
	${p.xsSize ? mq(EMediaQuery.xs, getMediaStyles(p.xsSize)) : ''}
  `;
};

// convert ETextColor to to theme color
const textColors = Object.values(ETextColor);
export const getTextColor = (p: any) => {
	for (const c of textColors) {
		if (c === p.textColor) {
			return `${p.theme.colors.text[c as keyof typeof p.theme.colors.text]};`;
		}
	}

	return `${p.theme.colors.text.white};`;
};

const themeColors = Object.values(EThemeColor);
export const getThemeColor = (theme: DefaultTheme, color: EThemeColor) => {
	for (const c of themeColors) {
		if (c === color) {
			return `${theme.colors[c as keyof typeof theme.colors]}`;
		}
	}

	return '';
};

export const getRarityColor = (theme: DefaultTheme, color: EItemRarity) => {
	switch (color) {
		case EItemRarity.common:
			return `${theme.colors.rarity.common}`;
		case EItemRarity.uncommon:
			return `${theme.colors.rarity.uncommon}`;
		case EItemRarity.rare:
			return `${theme.colors.rarity.rare}`;
		case EItemRarity.epic:
			return `${theme.colors.rarity.epic}`;
		case EItemRarity.legendary:
			return `${theme.colors.rarity.legendary}`;
		case EItemRarity.unique:
			return `${theme.colors.rarity.unique}`;
		default:
			return `${theme.colors.rarity.common}`;
	}
};

/**
 * Scroll in the view to an id
 * @param id target id
 * @param delta difference between top and id Y position
 */
export const scrollToId = (id: string, delta: number = 200) => {
	try {
		const rectTop = document.getElementById(id)?.getBoundingClientRect().top || 0;
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

		window.scrollTo({
			top: rectTop + scrollTop - delta,
			behavior: 'smooth'
		});
	} catch (err) {
		console.error('scrollToId', err);
	}
};

/**
 * Scroll in the view to an id
 * @param id target id
 */
export const scrollIntoViewToId = (id?: string) => {
	if (!id) {
		return;
	}

	try {
		const element = document.getElementById(id);

		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	} catch (err) {
		console.error('scrollIntoViewToId', err);
	}
};

// sonar-ignore-start
export const addPaddingStyles = (p: any) => {
	return `
		${p.xlPadding ? mq(EMediaQuery.xl, `padding: ${getSpacing(p.xlPadding)};`) : ''}
		${
			p.xlPaddingX
				? mq(EMediaQuery.xl, `padding-left: ${getSpacing(p.xlPaddingX)}; padding-right: ${getSpacing(p.xlPaddingX)};`)
				: ''
		}
		${
			p.xlPaddingY
				? mq(EMediaQuery.xl, `padding-top: ${getSpacing(p.xlPaddingY)}; padding-bottom: ${getSpacing(p.xlPaddingY)};`)
				: ''
		}
		${p.xlPaddingTop ? mq(EMediaQuery.xl, `padding-top: ${getSpacing(p.xlPaddingTop)};`) : ''}
		${p.xlPaddingBottom ? mq(EMediaQuery.xl, `padding-bottom: ${getSpacing(p.xlPaddingBottom)};`) : ''}
		${p.xlPaddingLeft ? mq(EMediaQuery.xl, `padding-left: ${getSpacing(p.xlPaddingLeft)};`) : ''}
		${p.xlPaddingRight ? mq(EMediaQuery.xl, `padding-right: ${getSpacing(p.xlPaddingRight)};`) : ''}

		${p.sxlPadding ? mq(EMediaQuery.sxl, `padding: ${getSpacing(p.sxlPadding)};`) : ''}
		${
			p.sxlPaddingX
				? mq(
						EMediaQuery.sxl,
						`padding-left: ${getSpacing(p.sxlPaddingX)}; padding-right: ${getSpacing(p.sxlPaddingX)};`
				  )
				: ''
		}
		${
			p.sxlPaddingY
				? mq(
						EMediaQuery.sxl,
						`padding-top: ${getSpacing(p.sxlPaddingY)}; padding-bottom: ${getSpacing(p.sxlPaddingY)};`
				  )
				: ''
		}
		${p.sxlPaddingTop ? mq(EMediaQuery.sxl, `padding-top: ${getSpacing(p.sxlPaddingTop)};`) : ''}
		${p.sxlPaddingBottom ? mq(EMediaQuery.sxl, `padding-bottom: ${getSpacing(p.sxlPaddingBottom)};`) : ''}
		${p.sxlPaddingLeft ? mq(EMediaQuery.sxl, `padding-left: ${getSpacing(p.sxlPaddingLeft)};`) : ''}
		${p.sxlPaddingRight ? mq(EMediaQuery.sxl, `padding-right: ${getSpacing(p.sxlPaddingRight)};`) : ''}


			${p.lgPadding ? mq(EMediaQuery.lg, `padding: ${getSpacing(p.lgPadding)};`) : ''}
			${
				p.lgPaddingX
					? mq(EMediaQuery.lg, `padding-left: ${getSpacing(p.lgPaddingX)}; padding-right: ${getSpacing(p.lgPaddingX)};`)
					: ''
			}
		${
			p.lgPaddingY
				? mq(EMediaQuery.lg, `padding-top: ${getSpacing(p.lgPaddingY)}; padding-bottom: ${getSpacing(p.lgPaddingY)};`)
				: ''
		}
		${p.lgPaddingTop ? mq(EMediaQuery.lg, `padding-top: ${getSpacing(p.lgPaddingTop)};`) : ''}
		${p.lgPaddingBottom ? mq(EMediaQuery.lg, `padding-bottom: ${getSpacing(p.lgPaddingBottom)};`) : ''}
		${p.lgPaddingLeft ? mq(EMediaQuery.lg, `padding-left: ${getSpacing(p.lgPaddingLeft)};`) : ''}
		${p.lgPaddingRight ? mq(EMediaQuery.lg, `padding-right: ${getSpacing(p.lgPaddingRight)};`) : ''}


	${p.mdPadding ? mq(EMediaQuery.md, `padding: ${getSpacing(p.mdPadding)};`) : ''}
			${
				p.mdPaddingX
					? mq(EMediaQuery.md, `padding-left: ${getSpacing(p.mdPaddingX)}; padding-right: ${getSpacing(p.mdPaddingX)};`)
					: ''
			}
		${
			p.mdPaddingY
				? mq(EMediaQuery.md, `padding-top: ${getSpacing(p.mdPaddingY)}; padding-bottom: ${getSpacing(p.mdPaddingY)};`)
				: ''
		}
		${p.mdPaddingTop ? mq(EMediaQuery.md, `padding-top: ${getSpacing(p.mdPaddingTop)};`) : ''}
		${p.mdPaddingBottom ? mq(EMediaQuery.md, `padding-bottom: ${getSpacing(p.mdPaddingBottom)};`) : ''}
		${p.mdPaddingLeft ? mq(EMediaQuery.md, `padding-left: ${getSpacing(p.mdPaddingLeft)};`) : ''}
		${p.mdPaddingRight ? mq(EMediaQuery.md, `padding-right: ${getSpacing(p.mdPaddingRight)};`) : ''}

		${p.smPadding ? mq(EMediaQuery.sm, `padding: ${getSpacing(p.smPadding)};`) : ''}
			${
				p.smPaddingX
					? mq(EMediaQuery.sm, `padding-left: ${getSpacing(p.smPaddingX)}; padding-right: ${getSpacing(p.smPaddingX)};`)
					: ''
			}
		${
			p.smPaddingY
				? mq(EMediaQuery.sm, `padding-top: ${getSpacing(p.smPaddingY)}; padding-bottom: ${getSpacing(p.smPaddingY)};`)
				: ''
		}
		${p.smPaddingTop ? mq(EMediaQuery.sm, `padding-top: ${getSpacing(p.smPaddingTop)};`) : ''}
		${p.smPaddingBottom ? mq(EMediaQuery.sm, `padding-bottom: ${getSpacing(p.smPaddingBottom)};`) : ''}
		${p.smPaddingLeft ? mq(EMediaQuery.sm, `padding-left: ${getSpacing(p.smPaddingLeft)};`) : ''}
		${p.smPaddingRight ? mq(EMediaQuery.sm, `padding-right: ${getSpacing(p.smPaddingRight)};`) : ''}

		${p.xsPadding ? mq(EMediaQuery.xs, `padding: ${getSpacing(p.xsPadding)};`) : ''}
			${
				p.xsPaddingX
					? mq(EMediaQuery.xs, `padding-left: ${getSpacing(p.xsPaddingX)}; padding-right: ${getSpacing(p.xsPaddingX)};`)
					: ''
			}
		${
			p.xsPaddingY
				? mq(EMediaQuery.xs, `padding-top: ${getSpacing(p.xsPaddingY)}; padding-bottom: ${getSpacing(p.xsPaddingY)};`)
				: ''
		}
		${p.xsPaddingTop ? mq(EMediaQuery.xs, `padding-top: ${getSpacing(p.xsPaddingTop)};`) : ''}
		${p.xsPaddingBottom ? mq(EMediaQuery.xs, `padding-bottom: ${getSpacing(p.xsPaddingBottom)};`) : ''}
		${p.xsPaddingLeft ? mq(EMediaQuery.xs, `padding-left: ${getSpacing(p.xsPaddingLeft)};`) : ''}
		${p.xsPaddingRight ? mq(EMediaQuery.xs, `padding-right: ${getSpacing(p.xsPaddingRight)};`) : ''}

			${p.padding ? `padding: ${getSpacing(p.padding)};` : ''}
		${p.paddingX ? `padding-left: ${getSpacing(p.paddingX)}; padding-right: ${getSpacing(p.paddingX)};` : ''}
		${p.paddingY ? `padding-top: ${getSpacing(p.paddingY)}; padding-bottom: ${getSpacing(p.paddingY)};` : ''}
		${p.paddingTop ? `padding-top: ${getSpacing(p.paddingTop)};` : ''}
		${p.paddingBottom ? `padding-bottom: ${getSpacing(p.paddingBottom)};` : ''}
		${p.paddingLeft ? `padding-left: ${getSpacing(p.paddingLeft)};` : ''}
		${p.paddingRight ? `padding-right: ${getSpacing(p.paddingRight)};` : ''}
	`;
};

export const addMarginStyles = (p: any) => {
	return `
		${p.xlMargin ? mq(EMediaQuery.xl, `margin: ${getSpacing(p.xlMargin)};`) : ''}
		${
			p.xlMarginX
				? mq(EMediaQuery.xl, `margin-left: ${getSpacing(p.xlMarginX)}; margin-right: ${getSpacing(p.xlMarginX)};`)
				: ''
		}
		${
			p.xlMarginY
				? mq(EMediaQuery.xl, `margin-top: ${getSpacing(p.xlMarginY)}; margin-bottom: ${getSpacing(p.xlMarginY)};`)
				: ''
		}
		${p.xlMarginTop ? mq(EMediaQuery.sxl, `margin-top: ${getSpacing(p.xlMarginTop)};`) : ''}
		${p.xlMarginBottom ? mq(EMediaQuery.sxl, `margin-bottom: ${getSpacing(p.xlMarginBottom)};`) : ''}
		${p.xlMarginLeft ? mq(EMediaQuery.sxl, `margin-left: ${getSpacing(p.xlMarginLeft)};`) : ''}
		${p.xlMarginRight ? mq(EMediaQuery.sxl, `margin-right: ${getSpacing(p.xlMarginRight)};`) : ''}

		${p.sxlMargin ? mq(EMediaQuery.sxl, `margin: ${getSpacing(p.sxlMargin)};`) : ''}
		${
			p.sxlMarginX
				? mq(EMediaQuery.sxl, `margin-left: ${getSpacing(p.sxlMarginX)}; margin-right: ${getSpacing(p.sxlMarginX)};`)
				: ''
		}
		${
			p.sxlMarginY
				? mq(EMediaQuery.sxl, `margin-top: ${getSpacing(p.sxlMarginY)}; margin-bottom: ${getSpacing(p.sxlMarginY)};`)
				: ''
		}
		${p.sxlMarginTop ? mq(EMediaQuery.sxl, `margin-top: ${getSpacing(p.sxlMarginTop)};`) : ''}
		${p.sxlMarginBottom ? mq(EMediaQuery.sxl, `margin-bottom: ${getSpacing(p.sxlMarginBottom)};`) : ''}
		${p.sxlMarginLeft ? mq(EMediaQuery.sxl, `margin-left: ${getSpacing(p.sxlMarginLeft)};`) : ''}
		${p.sxlMarginRight ? mq(EMediaQuery.sxl, `margin-right: ${getSpacing(p.sxlMarginRight)};`) : ''}


			${p.lgMargin ? mq(EMediaQuery.lg, `margin: ${getSpacing(p.lgMargin)};`) : ''}
			${
				p.lgMarginX
					? mq(EMediaQuery.lg, `margin-left: ${getSpacing(p.lgMarginX)}; margin-right: ${getSpacing(p.lgMarginX)};`)
					: ''
			}
		${
			p.lgMarginY
				? mq(EMediaQuery.lg, `margin-top: ${getSpacing(p.lgMarginY)}; margin-bottom: ${getSpacing(p.lgMarginY)};`)
				: ''
		}
		${p.lgMarginTop ? mq(EMediaQuery.lg, `margin-top: ${getSpacing(p.lgMarginTop)};`) : ''}
		${p.lgMarginBottom ? mq(EMediaQuery.lg, `margin-bottom: ${getSpacing(p.lgMarginBottom)};`) : ''}
		${p.lgMarginLeft ? mq(EMediaQuery.lg, `margin-left: ${getSpacing(p.lgMarginLeft)};`) : ''}
		${p.lgMarginRight ? mq(EMediaQuery.lg, `margin-right: ${getSpacing(p.lgMarginRight)};`) : ''}


	${p.mdMargin ? mq(EMediaQuery.md, `margin: ${getSpacing(p.mdMargin)};`) : ''}
			${
				p.mdMarginX
					? mq(EMediaQuery.md, `margin-left: ${getSpacing(p.mdMarginX)}; margin-right: ${getSpacing(p.mdMarginX)};`)
					: ''
			}
		${
			p.mdMarginY
				? mq(EMediaQuery.md, `margin-top: ${getSpacing(p.mdMarginY)}; margin-bottom: ${getSpacing(p.mdMarginY)};`)
				: ''
		}
		${p.mdMarginTop ? mq(EMediaQuery.md, `margin-top: ${getSpacing(p.mdMarginTop)};`) : ''}
		${p.mdMarginBottom ? mq(EMediaQuery.md, `margin-bottom: ${getSpacing(p.mdMarginBottom)};`) : ''}
		${p.mdMarginLeft ? mq(EMediaQuery.md, `margin-left: ${getSpacing(p.mdMarginLeft)};`) : ''}
		${p.mdMarginRight ? mq(EMediaQuery.md, `margin-right: ${getSpacing(p.mdMarginRight)};`) : ''}

		${p.smMargin ? mq(EMediaQuery.sm, `margin: ${getSpacing(p.smMargin)};`) : ''}
			${
				p.smMarginX
					? mq(EMediaQuery.sm, `margin-left: ${getSpacing(p.smMarginX)}; margin-right: ${getSpacing(p.smMarginX)};`)
					: ''
			}
		${
			p.smMarginY
				? mq(EMediaQuery.sm, `margin-top: ${getSpacing(p.smMarginY)}; margin-bottom: ${getSpacing(p.smMarginY)};`)
				: ''
		}
		${p.smMarginTop ? mq(EMediaQuery.sm, `margin-top: ${getSpacing(p.smMarginTop)};`) : ''}
		${p.smMarginBottom ? mq(EMediaQuery.sm, `margin-bottom: ${getSpacing(p.smMarginBottom)};`) : ''}
		${p.smMarginLeft ? mq(EMediaQuery.sm, `margin-left: ${getSpacing(p.smMarginLeft)};`) : ''}
		${p.smMarginRight ? mq(EMediaQuery.sm, `margin-right: ${getSpacing(p.smMarginRight)};`) : ''}

		${p.xsMarginTop ? mq(EMediaQuery.xs, `margin-top: ${getSpacing(p.xsMarginTop)};`) : ''}
		${p.xsMarginBottom ? mq(EMediaQuery.xs, `margin-bottom: ${getSpacing(p.xsMarginBottom)};`) : ''}
		${p.xsMarginLeft ? mq(EMediaQuery.xs, `margin-left: ${getSpacing(p.xsMarginLeft)};`) : ''}
		${p.xsMarginRight ? mq(EMediaQuery.xs, `margin-right: ${getSpacing(p.xsMarginRight)};`) : ''}

		${p.margin ? `margin: ${getSpacing(p.margin)};` : ''}
		${p.marginX ? `margin-left: ${getSpacing(p.marginX)}; margin-right: ${getSpacing(p.marginX)};` : ''}
		${p.marginY ? `margin-top: ${getSpacing(p.marginY)}; margin-bottom: ${getSpacing(p.marginY)};` : ''}
		${p.marginTop ? `margin-top: ${getSpacing(p.marginTop)};` : ''}
		${p.marginBottom ? `margin-bottom: ${getSpacing(p.marginBottom)};` : ''}
		${p.marginLeft ? `margin-left: ${getSpacing(p.marginLeft)};` : ''}
		${p.marginRight ? `margin-right: ${getSpacing(p.marginRight)};` : ''}
	`;
};

export const addTextColumnStyles = (p: any) => {
	return `
		${p.xlColumnCount ? mq(EMediaQuery.xl, `column-count: ${p.xlColumnCount};`) : ''}
		${p.lgColumnCount ? mq(EMediaQuery.lg, `column-count: ${p.lgColumnCount};`) : ''}
		${p.mdColumnCount ? mq(EMediaQuery.md, `column-count: ${p.mdColumnCount};`) : ''}
		${p.smColumnCount ? mq(EMediaQuery.sm, `column-count: ${p.smColumnCount};`) : ''}
		${p.columnCount ? `column-count: ${p.columnCount};` : ''}

		${p.xlColumnGap ? mq(EMediaQuery.xl, `column-gap: ${p.xlColumnGap};`) : ''}
		${p.lgColumnGap ? mq(EMediaQuery.lg, `column-gap: ${p.lgColumnGap};`) : ''}
		${p.mdColumnGap ? mq(EMediaQuery.md, `column-gap: ${p.mdColumnGap};`) : ''}
		${p.smColumnGap ? mq(EMediaQuery.sm, `column-gap: ${p.smColumnGap};`) : ''}
		${p.columnGap ? `column-gap: ${p.columnGap};` : ''}

		${p.xlColumnWidth ? mq(EMediaQuery.xl, `column-width: ${p.xlColumnWidth};`) : ''}
		${p.lgColumnWidth ? mq(EMediaQuery.lg, `column-width: ${p.lgColumnWidth};`) : ''}
		${p.mdColumnWidth ? mq(EMediaQuery.md, `column-width: ${p.mdColumnWidth};`) : ''}
		${p.smColumnWidth ? mq(EMediaQuery.sm, `column-width: ${p.smColumnWidth};`) : ''}
		${p.columnWidth ? `column-width: ${p.columnWidth};` : ''}

		${p.xlColumnFill ? mq(EMediaQuery.xl, `column-fill: ${p.xlColumnFill};`) : ''}
		${p.lgColumnFill ? mq(EMediaQuery.lg, `column-fill: ${p.lgColumnFill};`) : ''}
		${p.mdColumnFill ? mq(EMediaQuery.md, `column-fill: ${p.mdColumnFill};`) : ''}
		${p.smColumnFill ? mq(EMediaQuery.sm, `column-fill: ${p.smColumnFill};`) : ''}
		${p.columnFill ? `column-fill: ${p.columnFill};` : ''}
	`;
};

export const addFramesStyles = (p: any) => {
	return `
		${p.xlWidth ? mq(EMediaQuery.xl, `width: ${getSpacing(p.xlWidth)};`) : ``}
		${p.xlHeight ? mq(EMediaQuery.xl, `height: ${getSpacing(p.xlHeight)};`) : ``}
		${p.xlMinWidth ? mq(EMediaQuery.xl, `min-width: ${getSpacing(p.xlMinWidth)};`) : ``}
		${p.xlMaxWidth ? mq(EMediaQuery.xl, `max-width: ${getSpacing(p.xlMaxWidth)};`) : ``}
		${p.xlMinHeight ? mq(EMediaQuery.xl, `min-height: ${getSpacing(p.xlMinHeight)};`) : ``}
		${p.xlMaxHeight ? mq(EMediaQuery.xl, `max-height: ${getSpacing(p.xlMaxHeight)};`) : ``}

		${p.sxlWidth ? mq(EMediaQuery.sxl, `width: ${getSpacing(p.sxlWidth)};`) : ``}
		${p.sxlHeight ? mq(EMediaQuery.sxl, `height: ${getSpacing(p.sxlHeight)};`) : ``}
		${p.sxlMinWidth ? mq(EMediaQuery.sxl, `min-width: ${getSpacing(p.sxlMinWidth)};`) : ``}
		${p.sxlMaxWidth ? mq(EMediaQuery.sxl, `max-width: ${getSpacing(p.sxlMaxWidth)};`) : ``}
		${p.sxlMinHeight ? mq(EMediaQuery.sxl, `min-height: ${getSpacing(p.sxlMinHeight)};`) : ``}
		${p.sxlMaxHeight ? mq(EMediaQuery.sxl, `max-height: ${getSpacing(p.sxlMaxHeight)};`) : ``}

		${p.lgWidth ? mq(EMediaQuery.lg, `width: ${getSpacing(p.lgWidth)};`) : ``}
		${p.lgHeight ? mq(EMediaQuery.lg, `height: ${getSpacing(p.lgHeight)};`) : ``}
		${p.lgMinWidth ? mq(EMediaQuery.lg, `min-width: ${getSpacing(p.lgMinWidth)};`) : ``}
		${p.lgMaxWidth ? mq(EMediaQuery.lg, `max-width: ${getSpacing(p.lgMaxWidth)};`) : ``}
		${p.lgMinHeight ? mq(EMediaQuery.lg, `min-height: ${getSpacing(p.lgMinHeight)};`) : ``}
		${p.lgMaxHeight ? mq(EMediaQuery.lg, `max-height: ${getSpacing(p.lgMaxHeight)};`) : ``}

		${p.mdWidth ? mq(EMediaQuery.md, `width: ${getSpacing(p.mdWidth)};`) : ``}
		${p.mdHeight ? mq(EMediaQuery.md, `height: ${getSpacing(p.mdHeight)};`) : ``}
		${p.mdMinWidth ? mq(EMediaQuery.md, `min-width: ${getSpacing(p.mdMinWidth)};`) : ``}
		${p.mdMaxWidth ? mq(EMediaQuery.md, `max-width: ${getSpacing(p.mdMaxWidth)};`) : ``}
		${p.mdMinHeight ? mq(EMediaQuery.md, `min-height: ${getSpacing(p.mdMinHeight)};`) : ``}
		${p.mdMaxHeight ? mq(EMediaQuery.md, `max-height: ${getSpacing(p.mdMaxHeight)};`) : ``}

		${p.smWidth ? mq(EMediaQuery.sm, `width: ${getSpacing(p.smWidth)};`) : ``}
		${p.smHeight ? mq(EMediaQuery.sm, `height: ${getSpacing(p.smHeight)};`) : ``}
		${p.smMinWidth ? mq(EMediaQuery.sm, `min-width: ${getSpacing(p.smMinWidth)};`) : ``}
		${p.smMaxWidth ? mq(EMediaQuery.sm, `max-width: ${getSpacing(p.smMaxWidth)};`) : ``}
		${p.smMinHeight ? mq(EMediaQuery.sm, `min-height: ${getSpacing(p.smMinHeight)};`) : ``}
		${p.smMaxHeight ? mq(EMediaQuery.sm, `max-height: ${getSpacing(p.smMaxHeight)};`) : ``}

		${p.xsWidth ? mq(EMediaQuery.xs, `width: ${getSpacing(p.xsWidth)};`) : ``}
		${p.xsHeight ? mq(EMediaQuery.xs, `height: ${getSpacing(p.xsHeight)};`) : ``}
		${p.xsMinWidth ? mq(EMediaQuery.xs, `min-width: ${getSpacing(p.xsMinWidth)};`) : ``}
		${p.xsMaxWidth ? mq(EMediaQuery.xs, `max-width: ${getSpacing(p.xsMaxWidth)};`) : ``}
		${p.xsMinHeight ? mq(EMediaQuery.xs, `min-height: ${getSpacing(p.xsMinHeight)};`) : ``}
		${p.xsMaxHeight ? mq(EMediaQuery.xs, `max-height: ${getSpacing(p.xsMaxHeight)};`) : ``}

		${p.width ? `width: ${getSpacing(p.width)};` : ``}
		${p.height ? `height: ${getSpacing(p.height)};` : ``}
		${p.minWidth ? `min-width: ${getSpacing(p.minWidth)};` : ``}
		${p.maxWidth ? `max-width: ${getSpacing(p.maxWidth)};` : ``}
		${p.minHeight ? `min-height: ${getSpacing(p.minHeight)};` : ``}
		${p.maxHeight ? `max-height: ${getSpacing(p.maxHeight)};` : ``}
	`;
};

export const addOverflowStyles = (p: any) => css`
	${p.overflow ? `overflow: ${p.overflow};` : ``}
	${p.overflowX ? `overflow-x: ${p.overflowX};` : ``}
	${p.overflowY ? `overflow-y: ${p.overflowY};` : ``}

	${p.xlOverflow ? mq(EMediaQuery.xl, `overflow: ${p.xlOverflow};`) : ``}
	${p.xlOverflowX ? mq(EMediaQuery.xl, `overflow-x: ${p.xlOverflowX};`) : ``}
	${p.xlOverflowY ? mq(EMediaQuery.xl, `overflow-y: ${p.xlOverflowY};`) : ``}

	${p.sxlOverflow ? mq(EMediaQuery.sxl, `overflow: ${p.sxlOverflow};`) : ``}
	${p.sxlOverflowX ? mq(EMediaQuery.sxl, `overflow-x: ${p.sxlOverflowX};`) : ``}
	${p.sxlOverflowY ? mq(EMediaQuery.sxl, `overflow-y: ${p.sxlOverflowY};`) : ``}

	${p.lgOverflow ? mq(EMediaQuery.lg, `overflow: ${p.lgOverflow};`) : ``}
	${p.lgOverflowX ? mq(EMediaQuery.lg, `overflow-x: ${p.lgOverflowX};`) : ``}
	${p.lgOverflowY ? mq(EMediaQuery.lg, `overflow-y: ${p.lgOverflowY};`) : ``}

	${p.mdOverflow ? mq(EMediaQuery.md, `overflow: ${p.mdOverflow};`) : ``}
	${p.mdOverflowX ? mq(EMediaQuery.md, `overflow-x: ${p.mdOverflowX};`) : ``}
	${p.mdOverflowY ? mq(EMediaQuery.md, `overflow-y: ${p.mdOverflowY};`) : ``}

	${p.smOverflow ? mq(EMediaQuery.sm, `overflow: ${p.smOverflow};`) : ``}
	${p.smOverflowX ? mq(EMediaQuery.sm, `overflow-x: ${p.smOverflowX};`) : ``}
	${p.smOverflowY ? mq(EMediaQuery.sm, `overflow-y: ${p.smOverflowY};`) : ``}

	${p.xsOverflow ? mq(EMediaQuery.xs, `overflow: ${p.xsOverflow};`) : ``}
	${p.xsOverflowX ? mq(EMediaQuery.xs, `overflow-x: ${p.xsOverflowX};`) : ``}
	${p.xsOverflowY ? mq(EMediaQuery.xs, `overflow-y: ${p.xsOverflowY};`) : ``}
`;

// sonar-ignore-end

// get an enum type from a string value
export const getEnumFromValue = <T>(value: string | undefined, enumType: T): T[keyof T] => {
	try {
		if (!value) {
			throw new Error('Value is undefined');
		}

		const indexOfValue = Object.values(enumType as Object)
			.map((x) => x.toLowerCase())
			.indexOf(value.toLowerCase() as unknown as T[keyof T]);

		if (indexOfValue === -1) {
			throw new Error('Value does not exist in enum');
		}

		const key = Object.keys(enumType as Object)[indexOfValue] as keyof typeof enumType;

		return enumType[key];
	} catch (err) {
		// console.error('getEnumFromValue', err);

		// @ts-ignore
		return enumType.unset || enumType.none || null;
	}
};

// get an enum type from his key as string
export const getEnumFromKey = <T>(keyToSearch: string | undefined, enumType: T): T[keyof T] => {
	try {
		if (!keyToSearch) {
			throw new Error('Key is undefined');
		}

		const indexOfKey = Object.keys(enumType as Object)
			.map((itemKey) => itemKey.toLowerCase())
			.indexOf(keyToSearch.toLowerCase());

		if (indexOfKey === -1) {
			throw new Error('Key does not exist in enum');
		}

		const foundKey = Object.keys(enumType as Object)[indexOfKey] as keyof typeof enumType;

		return enumType[foundKey];
	} catch (err) {
		// @ts-ignore
		return enumType.unset || enumType.none || null;
	}
};

export const getETextColorFromETree = (tree: ETree | undefined): ETextColor => {
	switch (tree) {
		case ETree.solidity:
			return ETextColor.solidity;
		case ETree.general:
			return ETextColor.general;
		case ETree.cairo:
			return ETextColor.cairo;
		default:
			return ETextColor.white;
	}
};

export function pixelToRem(px: number) {
	return `${px / 16}rem`;
}

/**
 * The CSS rule for not displaying an element on the page
 */
const displayNoneRule = `display: none;`;

/**
 * Returns a string of media queries that will hide the element on the given ranges
 * @param ranges - Array of ranges to hide the element
 * @returns A string of media queries that will hide the element
 */
export const displayNone = (ranges: THiddenRange[]) => {
	const queries = [];
	for (const range of ranges) {
		if (range === undefined || (range[0] === undefined && range[1] === undefined)) {
			queries.push(displayNoneRule);
		} else {
			let query = '';

			if (range[0] === undefined && range[1]) {
				query = mq(range[1], displayNoneRule, 'min');
			} else if (range[1] === undefined && range[0]) {
				query = mq(range[0], displayNoneRule, 'max');
			} else if (range[0] && range[1]) {
				query = mqRange(range[0], range[1], displayNoneRule);
			}

			queries.push(`
				${query}
			`);
		}
	}

	return queries.join(';');
};

/**
 * Returns a string of media queries that will hide the element on the given hidden ranges from the props
 * @param p - Props that may contain hidden ranges
 * @returns A string of media queries that will hide the element
 */
export const addHiddenRangesStyles = (p: any) => {
	if (p.hiddenRange?.length) {
		return displayNone([p.hiddenRange]);
	}

	if (!p.hiddenRanges?.length) return ``;

	return displayNone(p.hiddenRanges);
};

// @todo(write tests)
export const preloadImages = (imageUrls: string[]) => {
	if (!imageUrls?.length) return;

	imageUrls.forEach((imageUrl) => {
		const existingLink = document.querySelector(`link[href="${imageUrl}"][as="image"]`);

		if (existingLink) return;

		const link = document.createElement('link');
		link.rel = 'preload';
		link.href = imageUrl;
		link.as = 'image';
		document.head.appendChild(link);
	});
};
